import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AnimatedTitle } from '@/components/animated-title'
import { AppearanceToggle } from '@/components/appearance-toggle'
import { MarketingAuthMenu } from '@/components/landing/marketing-auth-menu'
import { tenantBrandTypographyClass } from '@/components/client-brand'
import { TENANT_LOGO_MASK_FILL_CLASS, TenantLogoImg } from '@/components/tenant-logo-img'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useStickyNavScrolled } from '@/hooks/use-sticky-nav-scrolled'
import { LandingCtaLink } from '@/landing/landing-cta-link'
import { isLandingThemeToggleEnabled } from '@/landing/landing-theme-policy'
import { MARKETING_DIVIDER_BORDER } from '@/landing/landing-surfaces'
import {
  MARKETING_STICKY_NAV_FLOATING_PILL,
  MARKETING_STICKY_NAV_FLOATING_TOP,
} from '@/landing/layouts/landing-layouts'
import { MARKETING_HOME_LINK_CLASS } from '@/landing/marketing-home-link'
import { MarketingGuestHeaderBar } from '@/components/landing/marketing-guest-header-bar'
import { MarketingContentFrame } from '@/landing/marketing-content-frame'
import '@/landing/marketing-nav-fill.css'
import { resolveLanding, type ClientWithLanding } from '@/landing/resolve-landing'
import { cn } from '@/lib/utils'
import { CURRENT_CLIENT } from '@/constants'
import type { LandingNavLink } from '@/types/client-landing'

function MarketingBrandHeading({
  size = '3xl',
  overlay = false,
  overlayInverse = false,
  scrolled = false,
  marketingTheme,
}: {
  size?: 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl'
  overlay?: boolean
  overlayInverse?: boolean
  scrolled?: boolean
  marketingTheme?: 'light' | 'dark'
}) {
  const cc = CURRENT_CLIENT as {
    logoPath?: string
    logoPathLight?: string
    logoPathDark?: string
    brand?: string
    name?: string
  }
  const sizeClass =
    size === 'lg'
      ? 'text-lg'
      : size === 'xl'
        ? 'text-xl'
        : size === '2xl'
          ? 'text-2xl'
          : size === '3xl'
            ? 'text-3xl'
            : size === '4xl'
              ? 'text-4xl'
              : 'text-[2.55rem]'
  const label = cc.brand || cc.name || 'Home'
  const lightMode = marketingTheme !== 'dark'
  const overlayBrandTextClass =
    overlay && overlayInverse
      ? lightMode
        ? scrolled
          ? 'text-black'
          : 'text-white'
        : scrolled
          ? 'text-foreground'
          : 'text-white'
      : 'text-foreground'

  if (cc.logoPath || cc.logoPathLight || cc.logoPathDark) {
    return (
      <Link to="/" aria-label="Home" className={MARKETING_HOME_LINK_CLASS}>
        <TenantLogoImg
          logoOnLightBackground
          decorative
          className={`inline-block h-7 aspect-[123/27] ${TENANT_LOGO_MASK_FILL_CLASS} sm:h-8`}
        />
      </Link>
    )
  }

  return (
    <Link to="/" aria-label="Home" className={MARKETING_HOME_LINK_CLASS}>
      <h1
        className={cn(
          'm-0 truncate leading-none transition-colors duration-300 ease-in-out',
          tenantBrandTypographyClass(),
          sizeClass,
          overlayBrandTextClass
        )}
      >
        {label}
      </h1>
    </Link>
  )
}

export type MarketingHeaderSurfaceVariant = 'default' | 'cream'

export type MarketingPageHeaderProps = {
  signInLabel?: string
  registerLabel?: string
  hideRegister?: boolean
  hideSignIn?: boolean
  overlay?: boolean
  sticky?: boolean
  scrolled?: boolean
  authenticated?: boolean
  appHref?: string
  appLabel?: string
  marketingTheme?: 'light' | 'dark'
  marketingPreference?: 'light' | 'dark' | 'system'
  onMarketingThemeChange?: (t: 'light' | 'dark' | 'system') => void
  navLinks?: LandingNavLink[]
  mobileNavCta?: LandingNavLink
  brand?: ReactNode
  surfaceVariant?: MarketingHeaderSurfaceVariant
  className?: string
}

export function MarketingPageHeader({
  signInLabel = 'Sign in',
  registerLabel = 'Register',
  hideRegister = false,
  hideSignIn = false,
  overlay = false,
  sticky = false,
  scrolled: scrolledProp,
  authenticated = false,
  appHref = '/',
  appLabel = 'Go to app',
  marketingTheme,
  marketingPreference,
  onMarketingThemeChange,
  navLinks,
  mobileNavCta,
  brand,
  surfaceVariant = 'default',
  className,
}: MarketingPageHeaderProps) {
  const resolvedChrome = resolveLanding(CURRENT_CLIENT as ClientWithLanding).chrome
  const contentMaxWidth = resolvedChrome.contentMaxWidth
  const { ref: scrollRef, scrolled: fallbackScrolled } = useStickyNavScrolled(0)
  const scrolled = overlay ? (scrolledProp ?? fallbackScrolled) : false
  const showThemeToggle = isLandingThemeToggleEnabled()
  const showSolidBg = !overlay || scrolled || surfaceVariant === 'cream'
  const overlayInverse = resolvedChrome.overlayInverse
  const creamSurface = surfaceVariant === 'cream'
  /** Cream landings use `--marketing-*`; app landings (TalentCo etc.) use theme tokens. */
  const mutedNavClass = creamSurface
    ? 'text-[var(--marketing-muted,#667078)] hover:text-[var(--marketing-text,#1f2528)]'
    : 'text-muted-foreground hover:text-foreground'
  const overlayAuthLinkClass =
    overlay && overlayInverse && !scrolled
      ? 'text-white hover:text-white/90'
      : creamSurface
        ? mutedNavClass
        : 'text-foreground hover:text-foreground/80'

  const floatingHeader = resolvedChrome.headerStyle === 'floating'
  /** Fill via `.yeshuman-marketing-nav-fill` — uses `--marketing-nav-fill` when set (YesHuman). */
  const creamFillClass = 'yeshuman-marketing-nav-fill backdrop-blur-[16px]'
  const appGuestHeaderSurface = cn('bg-background', 'border-b', MARKETING_DIVIDER_BORDER)
  const headerFillClass =
    creamSurface
      ? creamFillClass
      : overlay && showSolidBg
        ? appGuestHeaderSurface
        : overlay
          ? 'bg-transparent'
          : appGuestHeaderSurface
  /** Full-bleed bar keeps a cream bottom border; floating pill uses fill only. */
  const headerSurfaceClass = creamSurface
    ? cn('border-b border-[var(--marketing-border,#e9e3d6)]/65', creamFillClass)
    : headerFillClass

  const authCluster = (
    <>
      {showThemeToggle ? (
        <div className="hidden shrink-0 items-center gap-4 md:flex">
          {creamSurface && navLinks && navLinks.length > 0 ? (
            <Separator
              orientation="vertical"
              variant="marketing"
              className="data-[orientation=vertical]:h-6"
              aria-hidden
            />
          ) : null}
          <AppearanceToggle
            variant="marketing"
            size={creamSurface ? 'compact' : 'default'}
            marketingTheme={marketingTheme}
            marketingPreference={marketingPreference}
            onMarketingThemeChange={onMarketingThemeChange}
            overlayTrack={overlay && !showSolidBg && resolvedChrome.themeToggleOverlayTrack}
          />
        </div>
      ) : null}
      {authenticated || !hideSignIn || !hideRegister ? (
        <nav
          className="flex items-center gap-3 text-sm font-medium sm:gap-4"
          aria-label="Account"
        >
          {authenticated ? (
            <MarketingAuthMenu appHref={appHref} appLabel={appLabel} />
          ) : (
            <>
              {!hideSignIn ? (
                <Link
                  to="/login"
                  className={cn(
                    'inline-flex h-9 shrink-0 items-center whitespace-nowrap underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm transition-[color] duration-300 ease-in-out',
                    overlayAuthLinkClass
                  )}
                >
                  {signInLabel}
                </Link>
              ) : null}
              {!hideRegister ? (
                <Button
                  asChild
                  variant={creamSurface ? 'marketingSolid' : 'default'}
                  size={creamSurface ? 'marketing' : 'default'}
                  pressFeedback
                >
                  <Link to="/register">{registerLabel}</Link>
                </Button>
              ) : null}
            </>
          )}
        </nav>
      ) : null}
    </>
  )

  const defaultBrand =
    resolvedChrome.brandMode === 'animated' ? (
      <AnimatedTitle
        logoOnLightBackground
        navigateHome
        logoClassName={`inline-block h-7 aspect-[123/27] ${TENANT_LOGO_MASK_FILL_CLASS} sm:h-8`}
      />
    ) : (
      <MarketingBrandHeading
        size={resolvedChrome.brandSize}
        overlay={overlay}
        overlayInverse={overlayInverse}
        scrolled={scrolled}
        marketingTheme={marketingTheme}
      />
    )

  const headerActions = (
    <>
      {navLinks && navLinks.length > 0 ? (
        <nav
          className={cn('hidden items-center gap-6 text-sm lg:flex', mutedNavClass)}
          aria-label="Sections"
        >
          {navLinks.map((link) => (
            <LandingCtaLink
              key={`${link.label}-${link.to}`}
              to={link.to}
              className="inline-flex items-center no-underline"
            >
              {link.label}
            </LandingCtaLink>
          ))}
        </nav>
      ) : null}
      {mobileNavCta ? (
        <LandingCtaLink
          to={mobileNavCta.to}
          className={cn(
            'inline-flex items-center text-sm font-medium no-underline lg:hidden',
            overlayAuthLinkClass
          )}
        >
          {mobileNavCta.label}
        </LandingCtaLink>
      ) : null}
      {authCluster}
    </>
  )

  const renderHeaderBar = (className?: string) => (
    <MarketingGuestHeaderBar
      brand={brand ?? defaultBrand}
      actions={headerActions}
      className={className}
    />
  )

  if (sticky) {
    return (
      /*
        Sticky + absolute (not fixed): share the scrollport content width so mx-auto
        columns line up with the hero. Fixed would center against the viewport and drift
        left/right by ~half the classic scrollbar gutter.
        h-0 wrapper keeps the bar overlaid without consuming layout height.
      */
      <div className="sticky top-0 z-50 h-0 w-full">
        <header
          ref={scrollRef}
          className={cn(
            'absolute inset-x-0 top-0 w-full shrink-0 transition-[background-color,border-color] duration-300 ease-in-out',
            floatingHeader ? 'bg-transparent' : headerSurfaceClass,
            className
          )}
        >
          {/*
            Floating pill: pad + negative margin on the inner row so the brand lines up
            with the hero while the fill still clears the rounded corners.
          */}
          <MarketingContentFrame
            maxWidth={contentMaxWidth}
            className={cn(floatingHeader && MARKETING_STICKY_NAV_FLOATING_TOP)}
          >
            {renderHeaderBar(
              floatingHeader ? cn(MARKETING_STICKY_NAV_FLOATING_PILL, headerFillClass) : undefined
            )}
          </MarketingContentFrame>
        </header>
      </div>
    )
  }

  return (
    <header
      ref={scrollRef}
      className={cn(
        'shrink-0 transition-[background-color] duration-300 ease-in-out',
        overlay
          ? showSolidBg
            ? headerSurfaceClass
            : 'bg-transparent'
          : headerSurfaceClass,
        className
      )}
    >
      <MarketingContentFrame maxWidth={contentMaxWidth}>{renderHeaderBar()}</MarketingContentFrame>
    </header>
  )
}
